/**
 * Streak intensity tiers — drive the "your feed is on fire" visuals as the
 * streak count climbs. Tier-based instead of a continuous ramp so the user
 * gets discrete, noticeable jumps as they cross day-count milestones.
 */
export interface StreakIntensity {
  // 0 = no streak; 1 = just lit; 5 = peak — drives a subtle radial glow
  // overlay on the page bg.
  pageGlow: number;
  // Box-shadow blur radius for the streak badge in px. Bigger = hotter.
  badgeGlowPx: number;
  // Box-shadow color alpha (0–1).
  badgeGlowAlpha: number;
  // Tremble animation duration. Shorter = faster vibration. "0s" disables.
  trembleDuration: string;
  // Max translation in px for the tremble keyframes. 0 = static.
  trembleAmplitude: number;
  // Hex/rgba pair for the badge gradient (start → end).
  gradientFrom: string;
  gradientTo: string;
}

/**
 * Saturation table is intentionally aggressive — most of the page is
 * covered by opaque card backgrounds, so the glow only reads when the
 * underlying gradient is strong enough to register through small gaps
 * AND drive the scroll container's bg + the header backdrop.
 */
export function getStreakIntensity(count: number): StreakIntensity {
  if (count >= 100) {
    return {
      pageGlow: 1.0,
      badgeGlowPx: 64,
      badgeGlowAlpha: 0.7,
      trembleDuration: "0.06s",
      trembleAmplitude: 1.6,
      gradientFrom: "rgba(255, 60, 30, 1)",
      gradientTo: "rgba(220, 0, 100, 1)",
    };
  }
  if (count >= 50) {
    return {
      pageGlow: 0.85,
      badgeGlowPx: 52,
      badgeGlowAlpha: 0.6,
      trembleDuration: "0.08s",
      trembleAmplitude: 1.3,
      gradientFrom: "rgba(255, 80, 35, 1)",
      gradientTo: "rgba(220, 30, 110, 1)",
    };
  }
  if (count >= 30) {
    return {
      pageGlow: 0.7,
      badgeGlowPx: 44,
      badgeGlowAlpha: 0.55,
      trembleDuration: "0.10s",
      trembleAmplitude: 1.0,
      gradientFrom: "rgba(255, 100, 40, 1)",
      gradientTo: "rgba(210, 50, 120, 1)",
    };
  }
  if (count >= 14) {
    return {
      pageGlow: 0.5,
      badgeGlowPx: 36,
      badgeGlowAlpha: 0.5,
      trembleDuration: "0.14s",
      trembleAmplitude: 0.7,
      gradientFrom: "rgba(255, 115, 45, 1)",
      gradientTo: "rgba(200, 60, 130, 1)",
    };
  }
  if (count >= 7) {
    return {
      pageGlow: 0.32,
      badgeGlowPx: 28,
      badgeGlowAlpha: 0.45,
      trembleDuration: "0.20s",
      trembleAmplitude: 0.4,
      gradientFrom: "rgba(255, 125, 50, 1)",
      gradientTo: "rgba(196, 70, 140, 1)",
    };
  }
  if (count >= 3) {
    return {
      pageGlow: 0.18,
      badgeGlowPx: 22,
      badgeGlowAlpha: 0.4,
      trembleDuration: "0s",
      trembleAmplitude: 0,
      gradientFrom: "rgba(255, 130, 55, 1)",
      gradientTo: "rgba(196, 80, 150, 1)",
    };
  }
  return {
    pageGlow: 0.08,
    badgeGlowPx: 16,
    badgeGlowAlpha: 0.35,
    trembleDuration: "0s",
    trembleAmplitude: 0,
    gradientFrom: "rgba(255, 145, 70, 1)",
    gradientTo: "rgba(196, 90, 160, 1)",
  };
}
